import { http } from "@network/http";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "noval";
import { useCallback, useMemo } from "react";

const AddAccountModal = dynamic(
  () => import("@components/modals/AddAccountModal/AddAccountModal"),
  { ssr: false }
);
const LogoutModal = dynamic(() => import("@components/modals/LogoutModal"), {
  ssr: false,
});
const AddStatementModal = dynamic(
  () => import("@components/modals/AddStatementModal"),
  { ssr: false }
);
const SuitabilityModal = dynamic(
  () => import("@components/modals/SuitableModal"),
  {
    ssr: false,
  }
);
const TermsModal = dynamic(() => import("@components/modals/TermsModal"), {
  ssr: false,
});
const DeleteAccount = dynamic(
  () => import("@components/modals/DeleteAccount"),
  {
    ssr: false,
  }
);
const ErrorModal = dynamic(
  () => import("@components/modals/DeleteAccount/ErrorModal"),
  { ssr: false }
);
const CookiesSettingsModal = dynamic(
  () => import("@components/modals/CookiesSettingsModal/CookiesSettingsModal"),
  { ssr: false }
);
const CookiesAlert = dynamic(() => import("@components/modals/CookiesAlert"), {
  ssr: false,
});
const SuccessModal = dynamic(
  () => import("@components/modals/SuccessModal/SuccessModal"),
  { ssr: false }
);
const AdsInModal = dynamic(
  () => import("@components/modals/AdsInModal/AdsInModal"),
  { ssr: false }
);
const AlertModal = dynamic(
  () => import("@components/modals/DeleteAccount/AlertModal"),
  { ssr: false }
);
const SurveyModal = dynamic(
  () => import("@components/modals/DeleteAccount/SurveyModal"),
  { ssr: false }
);
const EhsanConfirmModal = dynamic(
  () => import("@components/modals/EhsanConfirmModal"),
  {
    loading: () => <p>Loading...</p>,
  }
);

export const useRenderModal = (cookiesApproval, handleCookiesApproval) => {
  const { query } = useRouter();
  const { dispatch, addState } = useDispatch();
  const [deleteLoading, mainLoading] = useSelector([
    "deleteLoading",
    "mainLoading",
  ]);

  const isAppQuery = query.app === "true";
  const isCookiesOpen = !isAppQuery && !mainLoading && !cookiesApproval;

  const handleDeleteAccount = useCallback(async () => {
    dispatch({ deleteLoading: true });
    try {
      await http.post("/delete-account", {});
      Object.keys(window.localStorage).forEach((key) => {
        if (key !== "cookies_settings") {
          window.localStorage.removeItem(key);
        }
      });
      addState(
        {
          deletePopOpen: false,
          successModal: { isOpen: true, type: "delete_success" },
        },
        "modals"
      );
      dispatch({
        user: null,
        authed: false,
        deleteLoading: false,
      });
    } catch (error) {
      addState({ deleteLoading: false });
      dispatch(
        {
          deletePopOpen: false,
          deleteError: true,
        },
        "modals"
      );
    }
  }, []);

  const renderedModals = useMemo(() => {
    const onSuitabilityClose = () =>
      dispatch({ isOpen: false }, "modals.suitability");

    const modalsConfig = [
      {
        key: "successModal",
        subkey: true,
        disableClose: true,
        Modal: SuccessModal,
      },
      { key: "termsOpen", Modal: TermsModal },
      { key: "ehsanConfirm", subkey: true, Modal: EhsanConfirmModal },
      { key: "addStatementOpen", Modal: AddStatementModal },
      { key: "addAccountOpen", Modal: AddAccountModal },
      { key: "deleteAlert", Modal: DeleteAccount },
      { key: "surveyOpen", Modal: SurveyModal },
      {
        key: "deletePopOpen",
        Modal: AlertModal,
        isLoading: deleteLoading,
        onResolve: handleDeleteAccount,
      },
      { key: "deleteError", Modal: ErrorModal },
      { key: "adsInOpen", Modal: AdsInModal },
      { key: "logoutOpen", Modal: LogoutModal },
      {
        isOpen: isCookiesOpen,
        Modal: CookiesAlert,
        onCookiesApproved: handleCookiesApproval,
      },
      {
        key: "cookiesSettingsOpen",
        Modal: CookiesSettingsModal,
        onCookiesApproved: handleCookiesApproval,
      },
      {
        key: "suitability",
        subkey: true,
        Modal: SuitabilityModal,
        onClose: onSuitabilityClose,
      },
    ];

    return modalsConfig;
  }, [deleteLoading, isCookiesOpen]);

  return renderedModals;
};

export const RenderModal = ({ modal }) => {
  const { dispatch } = useDispatch();
  const current = useSelector(`modals.${modal?.key || "default"}`);

  const closeFallback = () => dispatch({ [modal.key]: false }, "modals");
  const isOpen = modal.isOpen ?? (modal.subkey ? current?.isOpen : current);
  const onClose = modal.onClose || closeFallback;

  if (!isOpen || !modal.Modal) return null;
  const { key, Modal, ...restProps } = modal;

  return (
    <modal.Modal
      key={key}
      isOpen={isOpen}
      {...(!modal.disableClose && { onClose })}
      {...current}
      {...restProps}
    />
  );
};
